import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";

interface RosterEditDrawerFooterProps {
  canRemove: boolean;
  onDismiss?: () => void;
  saving: boolean;
  dismissing: boolean;
  onCancel: () => void;
  onRemove: () => void;
}

export const RosterEditDrawerFooter = ({
  canRemove,
  onDismiss,
  saving,
  dismissing,
  onCancel,
  onRemove,
}: RosterEditDrawerFooterProps) => {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const savingEdits = saving && !dismissing;

  return (
    <Stack
      align="start"
      gap={2}
      className="w-full border-t border-line px-3 py-4"
    >
      {confirmingRemove ? (
        <Stack
          direction="row"
          align="center"
          justify="between"
          gap={2}
          className="w-full flex-wrap"
        >
          <Text variant="caption" tone="muted">
            Remove this person from the roster?
          </Text>
          <Stack direction="row" align="center" gap={2}>
            <Button
              type="button"
              size="xs"
              variant="secondary"
              disabled={saving}
              onClick={() => setConfirmingRemove(false)}
            >
              Keep
            </Button>
            <Button
              type="button"
              size="xs"
              color="danger"
              aria-busy={saving || undefined}
              disabled={saving}
              onClick={onRemove}
            >
              {saving && <Spinner data-icon="inline-start" aria-hidden />}
              Remove
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction="row"
          align="center"
          justify={canRemove || onDismiss ? "between" : "end"}
          gap={2}
          className="w-full flex-nowrap"
        >
          {canRemove && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              color="danger"
              disabled={saving}
              onClick={() => setConfirmingRemove(true)}
            >
              <TrashIcon data-icon="inline-start" />
              Remove from roster
            </Button>
          )}
          {onDismiss && (
            <Button
              type="button"
              size="xs"
              variant="ghost"
              disabled={saving || dismissing}
              aria-busy={dismissing || undefined}
              onClick={onDismiss}
            >
              {dismissing && <Spinner data-icon="inline-start" aria-hidden />}
              Not on payroll
            </Button>
          )}
          <Stack direction="row" align="center" gap={2} className="shrink-0">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={saving}
              onClick={onCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              aria-busy={savingEdits || undefined}
              disabled={saving}
            >
              {savingEdits && <Spinner data-icon="inline-start" aria-hidden />}
              {savingEdits ? "Saving roster..." : "Save changes"}
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
